/**
 * Branding for Morpheus RL
 * Central file for brand colors, typography, and styling guidelines
 * Use this for consistent branding across all components
 */

const Branding = {
  // Color Palette
  colors: {
    // Primary brand colors
    primaryBlue: '#3b82b8', // Medium blue - primary brand color
    deepPurple: '#391339', // Deep purple - secondary brand color
    darkSlate: '#3d4954', // Dark slate blue - headers and primary text
    
    // Interactive elements
    vibrantOrange: '#ea6d37', // Main CTA color
    teal: '#42909e', // Secondary action color
    seaGreen: '#5ba3ac', // Active elements
    tealBlue: '#297a92', // Links and important UI elements
    
    // Status indicators
    goldenOrange: '#e7ac54', // Warning state
    lightTeal: '#6aafb5', // Active state
    blueGreen: '#4d99a5', // Success state
    
    // Neutral tones
    slateGray: '#76909f', // Secondary text
    mutedMauve: '#8d7f88', // Subtle accents
    goldenTan: '#c59f65', // Warm accents for testimonials
    
    // Background shades
    pureWhite: '#fcfdfe',
    nearlyWhite: '#f5f9fa', 
    offWhite: '#ecf3f2',
    veryLightGray: '#d5dedd',
    
    // Additional accents
    paleBlue: '#abcaca',
    darkTeal: '#1b3f48',
    mustardYellow: '#ecc962',
    brightYellow: '#f8c341',
    turquoise: '#388899'
  },
  
  // Gradients
  gradients: {
    timerGradient: 'linear-gradient(to right, #6aafb5, #297a92)',
    ctaGradient: 'linear-gradient(to bottom, #ea6d37, #e7ac54)',
    featureGradient: 'linear-gradient(to bottom, #ecf3f2, #f5f9fa)',
    headerGradient: 'linear-gradient(135deg, #3d4954, #391339)',
    premiumGradient: 'linear-gradient(135deg, #391339, #1b3f48)',
    voiceGradient: 'linear-gradient(90deg, #297a92, #3b82b8)', // Updated for Mira interface
    miraGradient: 'linear-gradient(135deg, #297a92, #3b82b8)', // New gradient for Mira voice box
    progressGradient: 'linear-gradient(90deg, #6aafb5, #3b82b8)',
    warmGradient: 'linear-gradient(90deg, #ea6d37, #c59f65)',
    bannerGradient: 'linear-gradient(135deg, #3b82b8, #391339)',
    achievementGradient: 'linear-gradient(135deg, #ea6d37, #e7ac54)'
  },
  
  // Typography
  typography: {
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    headingFontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem'  // 36px
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700'
    }
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '2.5rem', // 40px
    '3xl': '3rem'    // 48px
  },
  
  // Borders & Shadows
  borders: {
    radius: {
      sm: '0.25rem',  // 4px
      md: '0.375rem', // 6px
      lg: '0.5rem',   // 8px
      xl: '1rem',     // 16px
      full: '9999px'  // Fully rounded (circles)
    },
    width: {
      thin: '1px',
      medium: '2px',
      thick: '3px'
    }
  },
  
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
  },
  
  // Transitions
  transitions: {
    default: '0.2s ease-in-out',
    slow: '0.3s ease-in-out',
    fast: '0.1s ease-in-out'
  },
  
  // Z-index scale
  zIndex: {
    base: 0,
    content: 10,
    nav: 20,
    popup: 30,
    modal: 40,
    toast: 50
  },

  // Standardized Component Patterns
  components: {
    // Clean Header Pattern
    cleanHeader: {
      container: {
        background: 'linear-gradient(135deg, #391339, #3d4954)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1.5rem',
        minHeight: '88px',
        maxHeight: '88px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      },
      logoSection: {
        minWidth: '72px'
      },
      logo: {
        height: '32px',
        width: 'auto',
        objectFit: 'contain',
        filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1))'
      },
      navigation: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center'
      },
      navLink: {
        base: {
          color: '#ffffff',
          textDecoration: 'none',
          padding: '0.5rem 1rem',
          borderRadius: '0.5rem',
          transition: 'all 0.2s ease',
          fontSize: '0.875rem',
          fontWeight: '500',
          background: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          whiteSpace: 'nowrap'
        },
        active: {
          background: 'rgba(255, 255, 255, 0.2)'
        }
      },
      profileSection: {
        minWidth: '72px',
        display: 'flex',
        justifyContent: 'flex-end',
        position: 'relative'
      },
      profileButton: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.5rem',
        padding: '0.75rem 1rem',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '0.875rem',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      },
      statusIndicator: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        connected: {
          backgroundColor: '#4d99a5',
          boxShadow: '0 0 6px rgba(77, 153, 165, 0.8)'
        },
        disconnected: {
          backgroundColor: '#FF5252',
          boxShadow: '0 0 6px rgba(255, 82, 82, 0.8)'
        }
      },
      userAvatar: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: '600'
      }
    },

    // Voice-First Interface Pattern
    voiceInterface: {
      container: {
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
        backgroundColor: '#f5f9fa'
      },
      centralArea: {
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto',
        background: '#f5f9fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: '500px',
        borderRadius: '0.5rem'
      }
    },

    // Standard Form Elements
    form: {
      input: {
        width: '100%',
        padding: '1rem',
        border: '1px solid #d5dedd',
        borderRadius: '0.375rem',
        fontSize: '1rem',
        transition: '0.2s ease-in-out'
      },
      button: {
        primary: {
          background: 'linear-gradient(to right, #6aafb5, #297a92)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '0.375rem',
          padding: '1rem',
          fontSize: '1rem',
          fontWeight: '500',
          cursor: 'pointer',
          transition: '0.2s ease-in-out'
        },
        secondary: {
          background: 'transparent',
          color: '#297a92',
          border: '1px solid #297a92',
          borderRadius: '0.375rem',
          padding: '1rem',
          fontSize: '1rem',
          fontWeight: '500',
          cursor: 'pointer',
          transition: '0.2s ease-in-out'
        }
      }
    },

    // Analytics Dashboard Components
    analytics: {
      dashboard: {
        container: {
          backgroundColor: '#fcfdfe',
          border: '1px solid #d5dedd',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '1rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        },
        widget: {
          backgroundColor: '#fcfdfe',
          border: '1px solid #d5dedd',
          borderRadius: '0.375rem',
          padding: '1rem',
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        },
        grid: {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        },
        header: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid #d5dedd'
        },
        title: {
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#3d4954',
          margin: 0
        },
        subtitle: {
          fontSize: '0.875rem',
          color: '#76909f',
          margin: 0
        }
      },
      chart: {
        container: {
          position: 'relative',
          height: '300px',
          padding: '1rem',
          backgroundColor: '#fcfdfe',
          borderRadius: '0.375rem'
        },
        tooltip: {
          backgroundColor: '#3d4954',
          color: '#fcfdfe',
          border: 'none',
          borderRadius: '0.375rem',
          padding: '0.5rem 1rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }
      },
      metric: {
        card: {
          backgroundColor: '#fcfdfe',
          border: '1px solid #d5dedd',
          borderRadius: '0.375rem',
          padding: '1rem',
          textAlign: 'center',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        },
        value: {
          fontSize: '1.875rem',
          fontWeight: '700',
          color: '#3d4954',
          lineHeight: 1.2
        },
        label: {
          fontSize: '0.875rem',
          color: '#76909f',
          fontWeight: '500',
          marginTop: '0.25rem'
        }
      }
    }
  },

  // Contextual Teaching Interface Patterns
  contextualTeaching: {
    // Sophisticated tool emergence patterns
    toolEmergence: {
      container: {
        position: 'relative',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transformOrigin: 'center center'
      },
      slideIn: {
        transform: 'translateY(0) scale(1)',
        opacity: 1,
        pointerEvents: 'auto'
      },
      slideOut: {
        transform: 'translateY(-20px) scale(0.95)',
        opacity: 0,
        pointerEvents: 'none'
      },
      fadeIn: {
        animation: 'morpheusToolFadeIn 0.6s ease-out forwards'
      }
    },
    
    // AI Lab appropriate sophisticated panels
    contextualPanel: {
      background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,249,250,0.98))',
      backdropFilter: 'blur(20px)',
      border: `1px solid rgba(61, 73, 84, 0.1)`,
      borderRadius: '12px',
      boxShadow: '0 20px 40px rgba(61, 73, 84, 0.08), 0 8px 16px rgba(61, 73, 84, 0.04)',
      padding: '24px',
      maxWidth: '480px',
      position: 'relative',
      overflow: 'hidden'
    },
    
    // Research lab appropriate progress indicators
    adaptiveProgress: {
      container: {
        background: 'linear-gradient(90deg, rgba(41, 122, 146, 0.1), rgba(107, 175, 181, 0.1))',
        border: `1px solid rgba(41, 122, 146, 0.2)`,
        borderRadius: '8px',
        padding: '16px',
        margin: '12px 0'
      },
      indicator: {
        height: '4px',
        background: 'linear-gradient(90deg, #297a92, #6aafb5)',
        borderRadius: '2px',
        transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
      },
      label: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#3d4954',
        marginBottom: '8px'
      }
    },
    
    // Sophisticated insight revelations
    insightReveal: {
      container: {
        background: 'linear-gradient(135deg, rgba(57, 19, 57, 0.03), rgba(61, 73, 84, 0.03))',
        border: `1px solid rgba(57, 19, 57, 0.1)`,
        borderRadius: '10px',
        padding: '20px',
        margin: '16px 0',
        position: 'relative',
        overflow: 'hidden'
      },
      glow: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, #391339, #3b82b8, #391339)',
        animation: 'morpheusInsightGlow 2s ease-in-out infinite'
      },
      content: {
        position: 'relative',
        zIndex: 1
      }
    }
  },

  // AI Lab Research-Grade Typography
  researchTypography: {
    // Academic paper styling for insights
    insight: {
      fontFamily: "'Inter', 'SF Pro Text', system-ui, sans-serif",
      fontSize: '15px',
      lineHeight: 1.6,
      color: '#3d4954',
      fontWeight: '400',
      letterSpacing: '-0.01em'
    },
    
    // Technical metric display
    metric: {
      fontFamily: "'SF Mono', 'Monaco', 'Cascadia Code', monospace",
      fontSize: '16px',
      fontWeight: '600',
      color: '#297a92',
      letterSpacing: '0.02em'
    },
    
    // Research findings header
    finding: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '18px',
      fontWeight: '600',
      color: '#391339',
      lineHeight: 1.4,
      letterSpacing: '-0.02em'
    }
  },

  // Helper functions for creating standardized components
  helpers: {
    /**
     * Creates a clean header following our design standards
     * Usage: const headerStyle = Branding.helpers.createCleanHeader();
     */
    createCleanHeader: () => ({
      container: Branding.components.cleanHeader.container,
      logoSection: Branding.components.cleanHeader.logoSection,
      logo: Branding.components.cleanHeader.logo,
      navigation: Branding.components.cleanHeader.navigation,
      profileSection: Branding.components.cleanHeader.profileSection,
      profileButton: Branding.components.cleanHeader.profileButton
    }),

    /**
     * Creates nav link styles with active state support
     * Usage: style={Branding.helpers.createNavLink(isActive)}
     */
    createNavLink: (isActive = false) => ({
      ...Branding.components.cleanHeader.navLink.base,
      ...(isActive ? Branding.components.cleanHeader.navLink.active : {})
    }),

    /**
     * Creates status indicator styles based on connection state
     * Usage: style={Branding.helpers.createStatusIndicator(isConnected)}
     */
    createStatusIndicator: (isConnected = false) => ({
      ...Branding.components.cleanHeader.statusIndicator,
      ...(isConnected ? 
        Branding.components.cleanHeader.statusIndicator.connected : 
        Branding.components.cleanHeader.statusIndicator.disconnected)
    }),

    /**
     * Creates primary form button styles
     * Usage: style={Branding.helpers.createButton('primary')}
     */
    createButton: (variant = 'primary') => {
      return Branding.components.form.button[variant] || Branding.components.form.button.primary;
    }
  }
};

export default Branding;